import { parseHtmlString } from '@bts-web/utils-functions';
import { css, stack } from '@bts-web/utils-style-engine';
import {
  getRoundedPrecision,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import { Locale } from '@bts-web/utils-lokalise';
import { LocalizedDate } from '../../common/components';
import { getAppConfig } from '@bts-web/core-app-config';
import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { SavingsPlanStatus } from '@bts-web/data-layer/server';
import { NonNullableSavingsPlanData } from './reusableTypes';
import {
  getSavingsPlansTranslations,
  SavingPlansTranslationsKeys,
} from '../../savings-plans/utils/getSavingsPlansTranslations';
import { AssetType, isBpfsAsset } from '../utils';

export type SavingsPlanMessage = {
  title: string;
  statusTitleIcon?: string;
  subtitle?: string;
  poweredByText?: string;
  statusContent?: ReactNode;
  secondaryButton?: {
    text: string;
    url: string;
  };
  primaryButton: {
    text: string;
    url: string;
  };
};

const prepareSavingsPlanContent = (
  savingsPlanData: NonNullableSavingsPlanData,
  translations: SavingPlansTranslationsKeys,
  locale: Locale,
  t: TFunction,
) => {
  const { asset, fiatAmount, nextRecurrence } = savingsPlanData;

  const { currency } = getAppConfig();

  const fiatAmountValue = intlFiatValueFormatting(
    getRoundedPrecision(fiatAmount),
    {
      locale,
      currency,
      fractionDigits: fiatAmount?.precision,
    },
  );

  return (
    <div
      className={stack({
        gap: 'extra_small_2',
        fontSize: 'body.medium',
        fontWeight: 'body.medium',
        letterSpacing: 'body.medium',
        lineHeight: 'body.medium',
        color: 'neutrals.text_secondary',
      })}
    >
      <div>
        <span>{translations.savingsPlanSuccessBodyLine1}</span>
        <span
          className={css({
            color: 'neutrals.text_primary',
          })}
        >
          &nbsp;
          {parseHtmlString(
            t('savings_plan_success_body_line_2', {
              amount: fiatAmountValue,
              assetName: asset?.name,
            }),
          )}
        </span>
        &nbsp;
        <span>{translations.savingsPlanSuccessBodyLine3}</span>
        <span
          className={css({
            color: 'neutrals.text_primary',
          })}
        >
          {nextRecurrence ? (
            <LocalizedDate date={nextRecurrence} locale={locale} />
          ) : null}
        </span>
      </div>

      <div>
        <span>{translations.savingsPlanSuccessBody2Line1}</span>
        <span
          className={css({
            color: 'neutrals.text_primary',
          })}
        >
          {translations.savingsPlanSuccessBody2Line2}
        </span>
        <span>{translations.savingsPlanSuccessBody2Line3}</span>
      </div>
    </div>
  );
};

export function getSavingsPlanMessages({
  translationUtil,
  locale,
  transactionData,
}: {
  translationUtil: TFunction;
  locale: Locale;
  transactionData: NonNullableSavingsPlanData;
}): Record<NonNullable<SavingsPlanStatus>, SavingsPlanMessage> {
  const translations = getSavingsPlansTranslations(translationUtil);

  const { asset } = transactionData;

  const isBpf = isBpfsAsset(asset!.__typename as AssetType);

  const savingsPlanId = transactionData.id;

  const savingsPlanName = transactionData.name;

  const statusContent = prepareSavingsPlanContent(
    transactionData,
    translations,
    locale,
    translationUtil,
  );

  return {
    ACTIVE: {
      title: translations.savingsPlanSuccessTitle,
      subtitle: `${savingsPlanName} ${translations.savingsPlanSuccessSubtitle}`,
      statusContent,
      poweredByText: isBpf ? translations.poweredByBpfs : undefined,
      primaryButton: {
        text: translations.savingsPlanSuccessCheckItOut,
        url: NEXT_ROUTES.SAVINGS_PLAN_DETAILS.replace('[id]', savingsPlanId),
      },
      secondaryButton: {
        text: translations.okGoToPortfolio,
        url: NEXT_ROUTES.PORTFOLIO,
      },
    },
    CANCELED: {
      title: '',
      subtitle: '',
      poweredByText: isBpf ? translations.poweredByBpfs : undefined,
      primaryButton: {
        text: translations.okGoToPortfolio,
        url: NEXT_ROUTES.PORTFOLIO,
      },
    },
  };
}
